import {oidcUserKey} from "../session-storage/session-storage";
import {apiBaseUrl} from "./config";
import axios, {type AxiosError, type AxiosRequestConfig, type AxiosResponse} from "axios";
import {HttpStatus} from "./http-status";
import {store} from "../store/store";
import {displayUserMessage} from "../components/message-panel/messagesSlice";
import {errorUserMessage} from "../components/message-panel/actions";
import {noAccessText} from "../components/configuration/edit/constants";
import {withTrailingSlash} from "./trailing-slash";

interface ApiRequest<T = object> extends Partial<AxiosRequestConfig> {
  url?: string;
  endpoint?: string;
  apiUrl?: string;
  data?: T;
  params?: Record<string, string>;
}

const api = axios.create();

const onErrorResponse = async (error: AxiosError | Error): Promise<AxiosError> => {
  if (axios.isAxiosError(error)) {
    const {status} = (error.response as AxiosResponse) ?? {};

    switch (status) {
      case HttpStatus.Forbidden: {
        store.dispatch(displayUserMessage(errorUserMessage(noAccessText).payload));
        break;
      }
      default: {
        break;
      }
    }
    return await Promise.reject(error);
  }
};

api.interceptors.response.use(undefined, onErrorResponse);

export const get = async (request: Partial<ApiRequest>) =>
  await api.get(buildRequestUrl(request), {
    headers: {...authorizationHeader()},
    params: request.params,
    ...(request.responseType && {responseType: request.responseType})
  });

export const post = async (request: Partial<ApiRequest>) =>
  await api.post(buildRequestUrl(request), request.data, {
    headers: {...authorizationHeader()},
    params: request.params
  });

export const put = async (request: Partial<ApiRequest>) =>
  await api.put(buildRequestUrl(request), request.data, {
    headers: {...authorizationHeader()},
    params: request.params
  });

export const patch = async (request: Partial<ApiRequest>) =>
  await api.patch(buildRequestUrl(request), request.data, {
    headers: {...authorizationHeader()},
    params: request.params
  });

export const deleteRequest = async (request: Partial<ApiRequest>) =>
  await api.delete(buildRequestUrl(request), {
    headers: {...authorizationHeader()},
    params: request.params
  });

const authorizationHeader = () => {
  const accessToken = JSON.parse(sessionStorage.getItem(oidcUserKey) ?? "{}")?.access_token;
  return {authorization: accessToken ? `Bearer ${accessToken}` : ""};
};

const buildRequestUrl = ({url, endpoint, apiUrl}: Partial<ApiRequest>): string =>
  url ?? buildUrl(endpoint, apiUrl) ?? EMPTY_TEXT;

const buildUrl = (endpoint?: string, apiUrl?: string): string | undefined =>
  endpoint ? withTrailingSlash(getApiUrl(apiUrl)) + endpoint : getApiUrl(apiUrl);

const getApiUrl = (apiUrl: string | undefined): string => apiUrl ?? apiBaseUrl;

const EMPTY_TEXT = "";
